import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Icon, Input, Pagination, Popover, Select, Space, Table, Tag, Text } from "@app/ui";
import { api } from "../api/client.js";
import { listAllOrgs } from "../api/dpp.js";
import { listUserDirectory } from "../api/platform.js";
import { useAuth } from "../auth/AuthContext.js";
import { RequirePermission } from "../components/RequirePermission.js";
import { ErrorAlert } from "../errors/index.js";
import { useFormatDateTime, useT } from "../i18n.js";
import { CreateUserModal } from "../users/CreateUserModal.js";
import { UserDetailModal } from "../users/UserDetailModal.js";
import { ShowDemoToggle, useShowDemo } from "../platform/useShowDemo.js";
import type { AdminUserRow, OrgWithCounts, Page, PlatformRole } from "../types.js";

const PAGE_SIZE = 20;

/**
 * Platform user directory: every account with its org memberships, platform
 * roles and last activity. Filters live in the URL (the overview links here
 * with `?withoutOrg=1` / `?status=disabled`); paging is server-side.
 */
export function UsersPage() {
  const t = useT();
  const fmt = useFormatDateTime();
  const { hasPermission, user: me } = useAuth();
  const [params, setParams] = useSearchParams();
  const { showDemo } = useShowDemo();
  const status = (params.get("status") ?? "") as "" | "active" | "disabled";
  const withoutOrg = params.get("withoutOrg") === "1";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [data, setData] = useState<Page<AdminUserRow> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [orgs, setOrgs] = useState<OrgWithCounts[]>([]);
  const [createOpen, setCreateOpen] = useState(params.get("new") === "1");
  const [detailId, setDetailId] = useState<string | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    next.delete("new");
    setParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await listUserDirectory({
          search: params.get("q") || undefined,
          status: status || undefined,
          withoutOrg,
          includeDemo: showDemo,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        }),
      );
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void load(), [params.toString(), showDemo]);

  useEffect(() => {
    // Pickers for the create/detail modals — only what the caller may read.
    if (hasPermission("role:read")) void api<PlatformRole[]>("/admin/roles").then(setRoles, () => {});
    if (hasPermission("org:manage")) void listAllOrgs(showDemo).then(setOrgs, () => {});
  }, [showDemo]);

  // Debounce typing so each keystroke doesn't hit the API.
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((params.get("q") ?? "") !== search.trim()) setParam("q", search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleDisabled = async (u: AdminUserRow) => {
    setError(null);
    try {
      await api<void>(`/admin/users/${u.id}/disable`, { method: "PATCH", body: { disabled: !u.disabled } });
      await load();
    } catch (e) {
      // Surfaces server-side guards (e.g. refusing to disable the last superadmin).
      setError(e);
    }
  };

  const del = async (u: AdminUserRow) => {
    try {
      await api<void>(`/admin/users/${u.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e);
    }
  };

  const rows = data?.items ?? [];

  return (
    <Card
      title={t("users.title")}
      extra={
        <RequirePermission permission="user:create">
          <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={() => setCreateOpen(true)}>
            {t("adminUsers.create")}
          </Button>
        </RequirePermission>
      }
    >
      {error != null && <ErrorAlert error={error} onRetry={() => void load()} />}
      <div className="flex flex-wrap items-center gap-3" style={{ marginBottom: "var(--az-sp-3)" }}>
        <div style={{ width: 300, maxWidth: "100%" }}>
          <Input
            prefix={<Icon name="search" size={14} />}
            placeholder={t("adminUsers.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ width: 180, maxWidth: "100%" }}>
          <Select
            options={[
              { label: t("adminOrgs.allStatuses"), value: "" },
              { label: t("users.active"), value: "active" },
              { label: t("users.disabled"), value: "disabled" },
            ]}
            value={status}
            onChange={(v) => setParam("status", v)}
          />
        </div>
        <div style={{ width: 200, maxWidth: "100%" }}>
          <Select
            options={[
              { label: t("adminUsers.anyOrg"), value: "" },
              { label: t("adminUsers.withoutOrg"), value: "1" },
            ]}
            value={withoutOrg ? "1" : ""}
            onChange={(v) => setParam("withoutOrg", v)}
          />
        </div>
        <ShowDemoToggle />
        {data && (
          <Text small secondary>
            {t("common.totalCount", { count: data.total })}
          </Text>
        )}
      </div>
      <Table
        rowKey={(u) => u.id}
        loading={loading && !data}
        empty={t("users.empty")}
        onRowClick={(u) => setDetailId(u.id)}
        columns={[
          {
            key: "user",
            title: t("adminUsers.user"),
            render: (u) => (
              <div className="min-w-0">
                <div className="font-medium">{u.displayName ?? u.email}</div>
                {u.displayName && (
                  <Text small secondary>
                    {u.email}
                  </Text>
                )}
              </div>
            ),
          },
          {
            key: "orgs",
            title: t("nav.adminOrgs"),
            render: (u) =>
              u.orgs.length === 0 ? (
                <Text secondary>—</Text>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {u.orgs.slice(0, 3).map((o) => (
                    <Tag key={o.id} color={o.role === "admin" ? "primary" : "default"}>
                      {o.name} · {t(`role.${o.role}`)}
                    </Tag>
                  ))}
                  {u.orgs.length > 3 && <Tag>+{u.orgs.length - 3}</Tag>}
                </div>
              ),
          },
          {
            key: "roles",
            title: t("adminUsers.platformRoles"),
            hideOnMobile: true,
            render: (u) => u.roles.join(", ") || "—",
          },
          {
            key: "status",
            title: t("common.status"),
            render: (u) => (
              <div className="flex flex-wrap gap-1">
                {u.disabled ? (
                  <Tag color="error">{t("users.disabled")}</Tag>
                ) : (
                  <Tag color="success">{t("users.active")}</Tag>
                )}
                {u.lockedUntil && <Tag color="warning">{t("adminUsers.locked")}</Tag>}
                {!u.emailVerified && <Tag color="warning">{t("adminUsers.emailUnverified")}</Tag>}
                {u.mfaEnabled && <Tag>{t("common.mfa")}</Tag>}
                {u.demo && <Tag color="primary">{t("platform.demoTag")}</Tag>}
              </div>
            ),
          },
          {
            key: "lastActive",
            title: t("adminUsers.lastActive"),
            hideOnMobile: true,
            render: (u) => fmt(u.lastActiveAt),
          },
          {
            key: "actions",
            title: "",
            render: (u) =>
              u.id === me?.id ? null : (
                // Row click opens the detail modal; the inline actions must not.
                <Space size="sm" onClick={(e) => e.stopPropagation()}>
                  <RequirePermission permission="user:update">
                    <Button size="sm" variant="subtle" onClick={() => void toggleDisabled(u)}>
                      {u.disabled ? t("users.enable") : t("users.disable")}
                    </Button>
                  </RequirePermission>
                  <RequirePermission permission="user:delete">
                    <Popover
                      placement="left"
                      title={t("users.deleteConfirm")}
                      content={
                        <Space direction="vertical">
                          <Text small secondary>
                            {t("users.deleteWarning")} {u.email}
                          </Text>
                          <Button size="sm" variant="danger" onClick={() => void del(u)}>
                            {t("users.deleteConfirmAction")}
                          </Button>
                        </Space>
                      }
                    >
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<Icon name="trash" size={14} />}
                        aria-label={t("users.delete")}
                      />
                    </Popover>
                  </RequirePermission>
                </Space>
              ),
          },
        ]}
        data={rows}
      />
      {data && data.total > PAGE_SIZE && (
        <div style={{ marginTop: "var(--az-sp-3)" }}>
          <Pagination
            total={data.total}
            pageSize={PAGE_SIZE}
            current={page}
            onChange={(p) => setParam("page", String(p))}
          />
        </div>
      )}
      <CreateUserModal
        open={createOpen}
        roles={roles}
        orgs={orgs}
        onClose={(created) => {
          setCreateOpen(false);
          if (created) void load();
        }}
      />
      <UserDetailModal
        user={rows.find((u) => u.id === detailId) ?? null}
        roles={roles}
        orgs={orgs}
        onClose={() => setDetailId(null)}
        onChanged={() => void load()}
      />
    </Card>
  );
}
